import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Gavel, Plus, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import {
  cargoActivo,
  baseMoraDe,
  esCargoPena,
  fmtFecha,
  fmtFechaCorta,
  fmtMXN,
  nuevoId,
  type Pago,
  type PenaManual,
} from "@/lib/operativo";
import { useOperativo } from "@/lib/operativo-store";
import { TablaReal } from "./TablaReal";
import { DesgloseAplicacion } from "./DesgloseAplicacion";

const INSTRUMENTOS_MONETARIOS = [
  "Transferencia",
  "Depósito bancario en efectivo moneda nacional",
  "Pago en efectivo en moneda nacional",
  "Pago en efectivo en dólares",
  "Pago en efectivo en otra divisa extranjera",
  "Efectivo",
  "Cheques",
  "Cheque de caja",
  "Cheques de viajero",
  "Pago con tarjeta",
  "Domiciliación o cargo recurrente",
  "Pago a través de otra entidad financiera",
  "Retención y descuento",
  "Dación en pago",
  "Bienes",
  "Derechos",
  "Oro o platino amonedados",
  "Plata amonedada",
  "Criptomonedas",
  "Se desconoce (tentativa)",
] as const;

const INSTRUMENTOS_ALTO_RIESGO = new Set<string>([
  "Pago en efectivo en dólares",
  "Pago en efectivo en otra divisa extranjera",
  "Oro o platino amonedados",
  "Plata amonedada",
  "Criptomonedas",
  "Se desconoce (tentativa)",
]);

const NOTA_RIESGO = "Instrumento de riesgo alto para PLD";

function Instrumento({ valor }: { valor: string }) {
  const altoRiesgo = INSTRUMENTOS_ALTO_RIESGO.has(valor);
  return (
    <span className="inline-flex items-center gap-1.5">
      {altoRiesgo && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="h-2 w-2 shrink-0 cursor-help rounded-full bg-chart-3"
              aria-label={NOTA_RIESGO}
            />
          </TooltipTrigger>
          <TooltipContent>{NOTA_RIESGO}</TooltipContent>
        </Tooltip>
      )}
      <span>{valor}</span>
    </span>
  );
}

export function RegistroPagos() {
  const {
    pagos,
    setPagos,
    penas,
    setPenas,
    programada,
    real,
    hoy,
    setHoy,
    cargarDemo,
    limpiar,
    solicitud,
    condiciones,
    cargosCredito,
  } = useOperativo();
  /**
   * Pena convencional DERIVADA de los cargos "Por evento de mora" de ESTA solicitud
   * (misma fuente única de verdad que el indicador de la solicitud).
   */
  const cargoPena = cargosCredito.find(
    (c) => c.productoId === solicitud.productoId && esCargoPena(c) && cargoActivo(c),
  );
  const penaSugerida = condiciones.penaConvencional;
  const [error, setError] = useState<string | null>(null);
  const [borrador, setBorrador] = useState<Omit<Pago, "id">>({
    fecha: programada[0]?.fechaProg ?? "",
    monto: 0,
    referencia: "",
    instrumento: "Transferencia",
  });
  /** Excepción: forzar el pago a un período concreto (rompe la regla de antigüedad). */
  const [manual, setManual] = useState(false);
  const [periodoManual, setPeriodoManual] = useState(1);
  const [errorPena, setErrorPena] = useState<string | null>(null);
  const [pena, setPena] = useState<Omit<PenaManual, "id">>({
    periodo: 1,
    fecha: programada[0]?.fechaProg ?? "",
    importe: penaSugerida,
    causaIva: cargoPena ? cargoPena.causaIva : true,
    referencia: "",
  });

  const agregarPena = () => {
    setErrorPena(null);
    if (!pena.fecha || !(pena.importe > 0)) {
      setErrorPena("Captura una fecha válida y un importe mayor a $0.00.");
      return;
    }
    setPenas([...penas, { ...pena, id: nuevoId("pena") }]);
  };

  const agregar = () => {
    setError(null);
    if (!borrador.fecha || !(borrador.monto > 0) || !borrador.instrumento) {
      setError("Captura una fecha válida, un monto mayor a $0.00 y el instrumento monetario.");
      return;
    }
    if (!solicitud.params.permitePagoAnticipado) {
      const fila = real.filas.find((f) => !f.liquidado) ?? real.filas[0];
      if (fila) {
        const exigible = fila.cuotaEsperada + fila.mora + fila.ivaMora + fila.pena + fila.ivaPena;
        const disponible = Math.max(0, exigible - fila.montoPagado);
        if (borrador.monto > disponible + 0.004) {
          setError(
            `Este producto no permite pago anticipado: el máximo aplicable al período ${fila.periodo} es ${fmtMXN(disponible)}.`,
          );
          return;
        }
      }
    }
    setPagos([
      ...pagos,
      {
        ...borrador,
        ...(manual ? { periodo: periodoManual } : {}),
        id: nuevoId("pago"),
      },
    ]);
    setBorrador({ ...borrador, monto: 0, referencia: "" });
  };

  const ordenados = [...pagos].sort((a, b) => a.fecha.localeCompare(b.fecha));
  const posterior = (fecha: string) => fecha > hoy;
  const vigentes = pagos.filter((p) => !posterior(p.fecha));
  const totalVigente = vigentes.reduce((a, p) => a + (Number.isFinite(p.monto) ? p.monto : 0), 0);
  /** Destino DERIVADO del pago: lo decide el motor, no la captura. */
  const aplicadoA = (p: Pago) => {
    if (posterior(p.fecha)) return "No aplicado (posterior al corte)";
    const a = real.aplicacionPagos[p.id];
    if (!a) return "—";
    const unicos = [...new Set(a.periodos)];
    const partes: string[] = [];
    if (unicos.length === 1) partes.push(`Período ${unicos[0]}`);
    else if (unicos.length > 1)
      partes.push(`Períodos ${unicos.slice(0, -1).join(", ")} y ${unicos[unicos.length - 1]}`);
    if (a.capitalExtra > 0.004) partes.push("abono a capital");
    if (a.sobrepago > 0.004) partes.push("sobrepago");
    return partes.length ? partes.join(" · ") : "—";
  };
  const NOTA_POSTERIOR =
    "No se considera en la tabla de aplicación ni en el estado de cuenta porque su fecha es posterior a la fecha de consulta.";

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
              Motor de aplicación de pagos
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              RN-06 prelación: 1° IVA pena → 2° pena → 3° IVA mora → 4° mora → 5° IVA interés → 6°
              interés → 7° capital. RN-07 mora desde el fin del período de gracia (base 360) sobre
              la base configurada: {baseMoraDe(solicitud.params)}. RN-08 pagos parciales dejan saldo
              insoluto arrastrado al siguiente período.
            </p>
          </div>
          <div className="no-print grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
            <Button size="sm" onClick={cargarDemo}>
              <Sparkles className="mr-1.5 h-4 w-4" /> Cargar escenario demo
            </Button>
            <Button size="sm" variant="outline" onClick={limpiar}>
              <RotateCcw className="mr-1.5 h-4 w-4" /> Limpiar pagos
            </Button>
          </div>
        </div>

        <Card className="print-block border-navy/15 p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <span className="h-4 w-1 rounded-full bg-emerald" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Registrar pago recibido
            </h3>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label htmlFor="p-fecha">Fecha de pago</Label>
              <Input
                id="p-fecha"
                type="date"
                value={borrador.fecha}
                onChange={(e) => setBorrador({ ...borrador, fecha: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-monto">Monto pagado (MXN)</Label>
              <Input
                id="p-monto"
                type="number"
                inputMode="decimal"
                step={0.01}
                className="num"
                value={Number.isNaN(borrador.monto) || borrador.monto === 0 ? "" : borrador.monto}
                onChange={(e) => setBorrador({ ...borrador, monto: e.target.valueAsNumber })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-ref">Referencia</Label>
              <Input
                id="p-ref"
                value={borrador.referencia}
                placeholder="TRANSF-001"
                onChange={(e) => setBorrador({ ...borrador, referencia: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-instrumento">Instrumento monetario</Label>
              <Select
                value={borrador.instrumento}
                onValueChange={(instrumento) => setBorrador({ ...borrador, instrumento })}
              >
                <SelectTrigger id="p-instrumento" aria-label="Instrumento monetario">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSTRUMENTOS_MONETARIOS.map((instrumento) => (
                    <SelectItem key={instrumento} value={instrumento}>
                      {instrumento}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-1">
              <Button className="no-print w-full" onClick={agregar}>
                <Plus className="mr-1.5 h-4 w-4" /> Registrar pago
              </Button>
            </div>
          </div>
          {error && <p className="mt-3 text-xs font-medium text-destructive">{error}</p>}
          <p className="mt-3 text-xs text-muted-foreground">
            El pago se aplica al período más vencido y el remanente continúa al siguiente. El
            sistema determina la aplicación.
          </p>
          <div className="mt-3 rounded-lg border border-dashed border-navy/20 px-4 py-3">
            <div className="flex items-center gap-2">
              <Switch id="p-manual" checked={manual} onCheckedChange={setManual} />
              <Label htmlFor="p-manual" className="text-xs leading-snug">
                Aplicación manual (avanzado)
              </Label>
            </div>
            {manual && (
              <div className="mt-3 space-y-1.5 sm:max-w-[12rem]">
                <Label htmlFor="p-periodo-manual">Forzar al período</Label>
                <Input
                  id="p-periodo-manual"
                  type="number"
                  inputMode="decimal"
                  min={1}
                  className="num"
                  value={Number.isNaN(periodoManual) ? "" : periodoManual}
                  onChange={(e) => setPeriodoManual(e.target.valueAsNumber)}
                />
                <p className="text-xs font-medium text-destructive">
                  Aplicación manual: rompe la regla de período más vencido.
                </p>
              </div>
            )}
          </div>
          {!solicitud.params.permitePagoAnticipado && (
            <p className="mt-3 text-xs text-muted-foreground">
              Pago anticipado deshabilitado en los parámetros del crédito: los pagos no pueden
              exceder el exigible del período.
            </p>
          )}
          {solicitud.params.penalidadPrepago && (
            <p className="mt-1 text-xs text-muted-foreground">
              Penalidad por prepago activa: {solicitud.params.penalidadPrepagoPct}% + IVA sobre el
              capital prepagado.
            </p>
          )}
          <div className="mt-4 space-y-1.5 sm:max-w-xs">
            <Label htmlFor="p-hoy">Fecha de consulta (hoy)</Label>
            <Input id="p-hoy" type="date" value={hoy} onChange={(e) => setHoy(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Determina días de atraso, mora al día y totales de liquidación.
            </p>
            <p className="text-xs font-medium text-navy">
              Solo se consideran movimientos con fecha menor o igual a esta fecha.
            </p>
          </div>
        </Card>

        <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-surface px-5 py-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Registrar pena convencional (captura manual)
            </h3>
            <Badge variant="secondary" className="num">
              {penas.length} pena(s)
            </Badge>
          </div>
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-1.5">
              <Label htmlFor="pena-periodo">Período</Label>
              <Input
                id="pena-periodo"
                type="number"
                inputMode="decimal"
                min={1}
                className="num"
                value={Number.isNaN(pena.periodo) ? "" : pena.periodo}
                onChange={(e) => {
                  const per = e.target.valueAsNumber;
                  const fila = programada.find((f) => f.periodo === per);
                  setPena({ ...pena, periodo: per, fecha: fila?.fechaProg ?? pena.fecha });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pena-fecha">Fecha de la pena</Label>
              <Input
                id="pena-fecha"
                type="date"
                value={pena.fecha}
                onChange={(e) => setPena({ ...pena, fecha: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pena-importe">Importe (MXN)</Label>
              <Input
                id="pena-importe"
                type="number"
                inputMode="decimal"
                step={0.01}
                className="num"
                value={Number.isNaN(pena.importe) ? "" : pena.importe}
                onChange={(e) => setPena({ ...pena, importe: e.target.valueAsNumber })}
              />
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-muted-foreground num">
                  {cargoPena ? `${cargoPena.nombre}: ` : "Pena del producto: "}
                  {fmtMXN(penaSugerida)}
                  {cargoPena?.causaIva ? " + IVA" : ""}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="no-print h-7 px-2 text-xs"
                  onClick={() =>
                    setPena({
                      ...pena,
                      importe: penaSugerida,
                      causaIva: cargoPena ? cargoPena.causaIva : pena.causaIva,
                    })
                  }
                >
                  Precargar
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch
                id="pena-iva"
                checked={pena.causaIva}
                onCheckedChange={(v) => setPena({ ...pena, causaIva: v })}
              />
              <Label htmlFor="pena-iva" className="text-xs leading-snug">
                ¿Causa IVA? {pena.causaIva ? "Sí" : "No"}
              </Label>
            </div>
            <div className="flex items-end">
              <Button variant="secondary" className="no-print w-full" onClick={agregarPena}>
                <Gavel className="mr-1.5 h-4 w-4" /> Registrar pena
              </Button>
            </div>
            {errorPena && (
              <p className="text-xs font-medium text-destructive sm:col-span-2 lg:col-span-5">
                {errorPena}
              </p>
            )}
            <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-5">
              La pena capturada entra a la prelación en los niveles 1 y 2 (IVA pena → pena) y al
              backlog si no se cubre; ya no se genera automáticamente al detectar mora.
            </p>
          </div>
          {penas.length > 0 && (
            <ul className="divide-y border-t">
              {[...penas]
                .sort((a, b) => a.fecha.localeCompare(b.fecha))
                .map((x) => (
                  <li key={x.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <div className="min-w-0">
                      <p className="num text-sm font-semibold text-navy">
                        Período {x.periodo} · {fmtMXN(x.importe)}
                        {x.causaIva ? " + IVA" : ""}
                      </p>
                      <p className="num text-xs text-muted-foreground">{fmtFechaCorta(x.fecha)}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Eliminar pena"
                      className="no-print shrink-0 text-destructive hover:text-destructive"
                      onClick={() => setPenas(penas.filter((y) => y.id !== x.id))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
            </ul>
          )}
        </Card>

        <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-surface px-5 py-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
              Pagos registrados
            </h3>
            <Badge variant="secondary" className="num">
              {vigentes.length} pagos · {fmtMXN(totalVigente)}
            </Badge>
          </div>
          {ordenados.length === 0 ? (
            <p className="px-5 py-6 text-sm text-muted-foreground">
              Aún no hay pagos registrados. Captura un pago o carga el escenario demo.
            </p>
          ) : (
            <>
              <ul className="solo-movil space-y-3 p-4 lg:hidden">
                {ordenados.map((p) => (
                  <li
                    key={p.id}
                    className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-navy/15 px-4 py-3 ${
                      posterior(p.fecha) ? "bg-muted opacity-60" : "bg-surface"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="num text-sm font-semibold text-navy">
                        {fmtMXN(p.monto)} · {aplicadoA(p)}
                      </p>
                      <p className="num truncate text-xs text-muted-foreground">
                        {fmtFecha(p.fecha)} · {p.referencia || "sin referencia"}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        <Instrumento valor={p.instrumento} />
                      </p>
                      {posterior(p.fecha) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="mt-1 text-[11px]">
                              Posterior al corte
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">{NOTA_POSTERIOR}</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Eliminar pago"
                      className="no-print shrink-0 text-destructive hover:text-destructive"
                      onClick={() => setPagos(pagos.filter((x) => x.id !== p.id))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
              <div className="solo-desktop hidden overflow-x-auto lg:block">
                <table className="w-full min-w-[860px] text-sm">
                  <thead>
                    <tr className="bg-navy text-primary-foreground">
                      <th className="px-3 py-2.5 text-left font-medium">Se aplicó a</th>
                      <th className="px-3 py-2.5 text-left font-medium">Fecha</th>
                      <th className="px-3 py-2.5 text-right font-medium">Monto</th>
                      <th className="px-3 py-2.5 text-left font-medium">Referencia</th>
                      <th className="px-3 py-2.5 text-left font-medium">Instrumento monetario</th>
                      <th className="no-print px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {ordenados.map((p) => (
                      <tr
                        key={p.id}
                        className={`border-b last:border-0 ${
                          posterior(p.fecha)
                            ? "bg-muted text-muted-foreground opacity-60"
                            : "odd:bg-surface/60"
                        }`}
                      >
                        <td className="px-3 py-2 num">{aplicadoA(p)}</td>
                        <td className="px-3 py-2 num text-muted-foreground">
                          {fmtFecha(p.fecha)}
                          {posterior(p.fecha) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="ml-2 text-[11px]">
                                  Posterior al corte
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">{NOTA_POSTERIOR}</TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right num font-semibold">
                          {fmtMXN(p.monto)}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{p.referencia || "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">
                          <Instrumento valor={p.instrumento} />
                        </td>
                        <td className="no-print px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setPagos(pagos.filter((x) => x.id !== p.id))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        <TablaReal />
        <DesgloseAplicacion />
      </div>
    </TooltipProvider>
  );
}
